import type { ReactNode } from 'react';
import { Icon } from '@/components/ui/Icon';

export function AiBadge({ children = 'AI' }: { children?: ReactNode }) {
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        padding: '2px 8px',
        borderRadius: 'var(--r-pill)',
        background: 'var(--text)',
        color: 'var(--bg)',
        fontSize: 'var(--t-xs)',
        fontWeight: 600,
      }}
    >
      <Icon name="ai" size={11} aria-hidden />
      {children}
    </span>
  );
}

export interface AiBlockProps {
  title?: string;
  children: ReactNode;
}

export function AiBlock({ title, children }: AiBlockProps) {
  return (
    <div
      style={{
        position: 'relative',
        padding: 'var(--pad-card)',
        borderRadius: 'var(--r-3)',
        // טוקנים (לא צבע קשיח) — כדי שבמצב כהה הבלוק לא יבהק.
        background: 'linear-gradient(135deg, var(--accent-soft), var(--surface-2))',
        border: '1px solid var(--rim-2)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <AiBadge />
        {title && (
          <span style={{ fontWeight: 600, fontSize: 'var(--t-sm)', color: 'var(--text)' }}>
            {title}
          </span>
        )}
      </div>
      <div style={{ color: 'var(--text-2)', fontSize: 'var(--t-md)', lineHeight: 1.6 }}>
        {children}
      </div>
    </div>
  );
}

export interface TranscriptLine {
  t: string;
  speaker: string;
  txt: string;
  sentiment?: 'positive' | 'neutral' | 'negative';
  mine?: boolean;
}

export interface TranscriptProps {
  lines: TranscriptLine[];
  lang?: 'he' | 'en';
  highlight?: string[];
  /** שמות הדוברים (מה-DB/CRM או שחולצו מהשיחה) — "נציג · דורון". */
  names?: { agent?: string | null; customer?: string | null } | null;
}

// תווית דובר קריאה: agent/customer → עברית, ושם לצידה כשידוע. ערך אחר
// (תמלולי סוכן קולי וכו') מוצג כמו שהוא.
function speakerLabel(speaker: string, names?: TranscriptProps['names']): string {
  if (speaker === 'agent') return names?.agent ? `נציג · ${names.agent}` : 'נציג';
  if (speaker === 'customer') return names?.customer ? `לקוח · ${names.customer}` : 'לקוח';
  return speaker;
}

export function Transcript({ lines, lang = 'he', highlight = [], names }: TranscriptProps) {
  if (!lines?.length) {
    return (
      <div style={{ color: 'var(--muted)', textAlign: 'center', padding: 24, fontSize: 'var(--t-sm)' }}>
        {lang === 'he' ? 'אין תמלול' : 'No transcript'}
      </div>
    );
  }

  return (
    <div className="stack" style={{ gap: 8 }}>
      {lines.map((line, i) => (
        <div
          key={i}
          style={{
            display: 'flex',
            gap: 12,
            padding: 'var(--pad-row)',
            background: line.mine ? 'var(--accent-soft)' : 'var(--surface-2)',
            borderRadius: 'var(--r-2)',
            borderInlineStart: `3px solid ${sentimentColor(line.sentiment)}`,
          }}
        >
          <span style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)', fontFamily: 'var(--font-mono)', whiteSpace: 'nowrap' }}>
            {line.t}
          </span>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 'var(--t-xs)', color: 'var(--text-2)', marginBottom: 2 }}>
              {speakerLabel(line.speaker, names)}
            </div>
            <div style={{ fontSize: 'var(--t-sm)' }}>{highlightText(line.txt, highlight)}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

function sentimentColor(s?: 'positive' | 'neutral' | 'negative'): string {
  if (s === 'positive') return 'var(--ok)';
  if (s === 'negative') return 'var(--danger)';
  return 'var(--line)';
}

function highlightText(text: string, terms: string[]): ReactNode {
  if (!terms.length) return text;
  const pattern = new RegExp(`(${terms.join('|')})`, 'gi');
  const parts = text.split(pattern);

  return parts.map((part, i) =>
    pattern.test(part) ? (
      <mark key={i} style={{ background: 'var(--warn-soft)', color: 'var(--warn)', padding: '0 2px', borderRadius: 2 }}>
        {part}
      </mark>
    ) : (
      part
    )
  );
}
